import { useState } from "react";
import { Star, ChevronLeft, ChevronRight, Quote } from "lucide-react";

const REVIEWS = [
  {
    name: "Ashiq Rahman",
    initials: "AR",
    text: "Very professional and friendly staff. The doctor explained every step clearly. My root canal was completely painless. Highly recommend Abhayam Dental!",
    rating: 5,
    time: "2 weeks ago",
  },
  {
    name: "Sneha Menon",
    initials: "SM",
    text: "Got my braces done here. The clinic is spotlessly clean and the team is genuinely caring. Great value for money too.",
    rating: 5,
    time: "1 month ago",
  },
  {
    name: "Rahul Kumar",
    initials: "RK",
    text: "Best dental clinic in Manjeri. Modern equipment, transparent pricing and gentle treatment for my kids. Very happy with the results.",
    rating: 5,
    time: "1 month ago",
  },
  {
    name: "Ayesha Sultana",
    initials: "AS",
    text: "Excellent teeth whitening treatment. Doctor was patient with all my questions. Will definitely be back for regular check-ups.",
    rating: 4,
    time: "2 months ago",
  },
  {
    name: "Vishnu Prakash",
    initials: "VP",
    text: "Went in for an emergency extraction on a Saturday evening. They accommodated me right away. Absolutely lifesaving service.",
    rating: 5,
    time: "3 months ago",
  },
  {
    name: "Fathima Beevi",
    initials: "FB",
    text: "The staff is very polite and the clinic hygiene is top notch. My daughter loves visiting Dr. Fathima — she is amazing with kids.",
    rating: 5,
    time: "3 months ago",
  },
];

export const Testimonials = () => {
  const [idx, setIdx] = useState(0);
  const perView = 3;
  const maxIdx = Math.max(0, REVIEWS.length - perView);

  const prev = () => setIdx((i) => (i > 0 ? i - 1 : maxIdx));
  const next = () => setIdx((i) => (i < maxIdx ? i + 1 : 0));

  return (
    <section
      id="reviews"
      data-testid="testimonials-section"
      className="relative py-20 sm:py-28"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex flex-wrap items-end justify-between gap-6 mb-12 reveal">
          <div className="max-w-2xl">
            <div className="inline-flex items-center gap-2 px-3.5 py-1.5 rounded-full bg-[#4FC3F7]/15 text-[#0288d1] text-xs font-semibold tracking-wide uppercase">
              Patient Stories
            </div>
            <h2 className="mt-5 text-4xl sm:text-5xl font-semibold tracking-tight text-[#1F2937] leading-[1.05]">
              Loved by patients across{" "}
              <span className="text-[#009688]">Manjeri</span>.
            </h2>
            <div className="mt-5 flex items-center gap-3">
              <div className="flex">
                {[1, 2, 3, 4, 5].map((s) => (
                  <Star key={s} className="w-5 h-5 fill-amber-400 text-amber-400" />
                ))}
              </div>
              <div className="text-sm text-[#1F2937]/70">
                <span className="font-bold text-[#1F2937]">4.3</span> · 18 Google
                Reviews
              </div>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <button
              data-testid="reviews-prev"
              onClick={prev}
              className="w-12 h-12 rounded-full bg-white border border-[#009688]/20 grid place-items-center hover:bg-[#009688] hover:text-white transition-colors"
              aria-label="Previous review"
            >
              <ChevronLeft className="w-5 h-5" />
            </button>
            <button
              data-testid="reviews-next"
              onClick={next}
              className="w-12 h-12 rounded-full bg-[#009688] text-white grid place-items-center hover:bg-[#00796b] transition-colors"
              aria-label="Next review"
            >
              <ChevronRight className="w-5 h-5" />
            </button>
          </div>
        </div>

        <div className="overflow-hidden">
          <div
            className="flex gap-5 transition-transform duration-700 ease-out"
            style={{
              transform: `translateX(calc(-${idx} * (100% / ${perView}) - ${idx} * 20px / ${perView}))`,
            }}
          >
            {REVIEWS.map((r, i) => (
              <article
                key={i}
                data-testid={`review-card-${i}`}
                className="w-full sm:w-1/2 lg:w-1/3 shrink-0 bg-white rounded-2xl border border-[#009688]/10 p-6 hover:shadow-[0_20px_40px_rgba(0,0,0,0.06)] transition-shadow"
              >
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className="w-11 h-11 rounded-full bg-gradient-to-br from-[#009688] to-[#00796b] text-white grid place-items-center font-bold text-sm">
                      {r.initials}
                    </div>
                    <div>
                      <div className="font-semibold text-[#1F2937] text-[15px]">
                        {r.name}
                      </div>
                      <div className="text-xs text-[#1F2937]/50">{r.time}</div>
                    </div>
                  </div>
                  <Quote className="w-6 h-6 text-[#009688]/30" />
                </div>

                <div className="mt-4 flex gap-0.5">
                  {[1, 2, 3, 4, 5].map((s) => (
                    <Star
                      key={s}
                      className={`w-4 h-4 ${
                        s <= r.rating
                          ? "fill-amber-400 text-amber-400"
                          : "text-gray-200"
                      }`}
                    />
                  ))}
                </div>

                <p className="mt-3 text-[14.5px] text-[#1F2937]/75 leading-relaxed">
                  {r.text}
                </p>

                <div className="mt-5 pt-4 border-t border-[#009688]/8 flex items-center gap-2">
                  <svg className="w-4 h-4" viewBox="0 0 48 48">
                    <path
                      fill="#4285F4"
                      d="M24 9.5c3.9 0 6.6 1.7 8.1 3.1l6-5.8C34.4 3.3 29.6 1 24 1 14.9 1 7.2 6.3 3.7 14l6.9 5.4C12.4 13.7 17.7 9.5 24 9.5z"
                    />
                    <path
                      fill="#34A853"
                      d="M46.5 24.5c0-1.6-.1-2.8-.4-4H24v7.6h12.7c-.3 2-1.7 5.1-4.9 7.2l7.5 5.8c4.4-4.1 7.2-10.1 7.2-16.6z"
                    />
                    <path
                      fill="#FBBC05"
                      d="M10.6 28.6c-.6-1.7-.9-3.4-.9-5.1s.3-3.4.9-5.1L3.7 13c-1.7 3.4-2.7 7.2-2.7 11 0 3.9 1 7.7 2.7 11l6.9-5.4z"
                    />
                    <path
                      fill="#EA4335"
                      d="M24 47c6.5 0 11.9-2.1 15.9-5.9l-7.5-5.8c-2.1 1.5-4.9 2.5-8.4 2.5-6.3 0-11.7-4.2-13.5-9.9l-6.9 5.4C7.2 41.7 14.9 47 24 47z"
                    />
                  </svg>
                  <span className="text-xs text-[#1F2937]/60 font-medium">
                    Posted on Google
                  </span>
                </div>
              </article>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};
